// @ts-nocheck
// Removed unused imports: useEffect, useMemo, Button, Card, CardContent, IconButton, Paper, Snackbar, Typography, Translate, 
// DoughnutChart, postErrorSlack, yearsFromDate, TimeTable, SearchTable, HospitalStats, PatientsBarChart, 
// OutpatientsStats, CommonDiagnosis, BillingChart, BillingInsuranceBars, SurveyResearcherStats, FUNCTIONALITY, 
// and also removed styled(MuiDivider) + Divider import from "@mui/material", since it was not used.
// Removed unused analyticsTabs from config.
// Removed unused variables: departmentSelected (never used), statsFirstMonitoring, loadingStatsFirstMonitoring, 
// mostCommonDiagnosis, appointmentsPerDepartment, billingDepartments, billingInsurances (they were declared 
// but not referenced in the component).

// Analytics/index.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from "react-helmet";
import PropTypes from 'prop-types';
import { green, red, orange, yellow, blue, amber, brown, cyan, deepOrange } from "@mui/material/colors";
import { Box, Grid, Chip, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { connect } from 'react-redux';
import Loader from '../../../components/Loader';
import { withLocalize, Translate } from 'react-localize-redux';
import { ROUTE_401 } from '../../../routes/urls';
import styled, { withTheme } from "styled-components";
import { spacing } from "@mui/system";
import DatesSelector from '../../dashboards/Analytics/DatesSelector';

import { useDeparmentsSelector, useInsurancesList } from '../../../hooks';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import SectionHeader from '../../components/SectionHeader';
import AnalyticsHeader from './AnalyticsHeader';
import TabContent from './TabContent';

import usePatientFilter from './hooks/usePatientFilter';
import { LIST_COLORS, ageGroups, COUNT_AGE } from './config/constants';
import { getAnalyticsTabs } from './config/analyticsConfig';

export function Analytics(props) {
  // SHER-854: this page deliberately does NOT call usePatients(). That hook runs the
  // non-silent bulk download, which raises the full-viewport blocking overlay in
  // layouts/Dashboard.jsx (state.patients.downloading) and held the whole dashboard
  // for ~1 min on a 53k-patient tenant before a single chart was drawn. Analytics
  // reads server-side aggregates from /analytics/<uuid>/..., and its patient value
  // was never read here anyway. The two panels that do need decrypted patients —
  // Age Group and Sex Distribution, and the Diagnosis table, both on Outpatients —
  // fetch them from useOutpatientsData/useInpatientsData with the silent variant,
  // so they fill in when the data lands instead of blocking the page. Do not
  // reintroduce a page-level fetch to "warm the cache".
  const history = useHistory();
  
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const [activeTabId, setActiveTabId] = useState(null);
  // This hook is used, but we aren't referencing the insurances data in the UI yet.
  const [insurances] = useInsurancesList();

  const [activityPatients, setActivityPatients] = useState([]);
  const [countSex, setCountSex] = useState({ male: 0, female: 0 });
  
  // Keep age-based state if it’s relevant for future logic
  const [countAge, setCountAge] = useState([...COUNT_AGE]);

  // Only used to conditionally render department selectors
  const onlyDepartmentsResearcher =
    props.investigations?.currentInvestigation &&
    props.investigations.currentInvestigation.permissions?.includes(
      PERMISSION.ANALYTICS_DEPARTMENT
    );

  const {
    renderDepartmentSelector,
    departments,
    departmentSelected,
  } = useDeparmentsSelector(true, onlyDepartmentsResearcher, true, "all");

  // Use custom hook to filter patients based on department selection
  const { loadingPatientsInfo, filteredPatients } = usePatientFilter(
    props.investigations?.currentInvestigation,
    departmentSelected,
    startDate,
    endDate,
  );

  console.log("departments", departments, departmentSelected, startDate, endDate, filteredPatients);
  // For controlling which tab is active on AnalyticsHeader

  function datesSelected(dates) {
    console.log("We have dates", dates);
    setStartDate(dates[0].getTime());
    setEndDate(dates[1].getTime());
  }

  function renderSelectors() {
    if (departments === null) {
      return (
        <Grid item xs={12}>
          <Loader />
        </Grid>
      );
    }

    if (departments.length === 1) {
      return (
        <Grid item xs={12}>
          <DatesSelector onCallBack={datesSelected} />
        </Grid>
      );
    }

    return (
      <Box width="100%" sx={{ padding: 2 }}>
        <Box minWidth="100%" display={{ xs: 'flex', sm: 'flex' }} flexDirection={{ xs: 'column', md: 'row' }}  gap={2}>
          {/* Department Selector */}
          <Box minWidth={{ xs: '100%', md: '200px' }} >
            {renderDepartmentSelector()}
          </Box>
    
          {/* Date Selector with fixed layout */}
          <Box minWidth={{ xs: '100%', md: 'auto' }}  display="flex" alignItems="top">
            <DatesSelector onCallBack={datesSelected} />
          </Box>
        </Box>
      </Box>
    );
    
  }

  const investigationFunctionalities =
    props.investigations?.currentInvestigation?.functionalities;

  const visibleTabs = useMemo(
    () => getAnalyticsTabs(props.investigations?.currentInvestigation),
    [investigationFunctionalities]
  );

  useEffect(() => {
    if (!visibleTabs.length) {
      setActiveTabId(null);
      return;
    }

    setActiveTabId((currentTabId) => {
      if (currentTabId && visibleTabs.some((tab) => tab.id === currentTabId)) {
        return currentTabId;
      }
      return visibleTabs[0].id;
    });
  }, [visibleTabs]);

  // If investigations is not available or still loading
  if (!props.investigations || props.investigations.loading || !props.investigations.currentInvestigation) {
    return <Loader />;
  }
  // If the user lacks the necessary permissions
  else if (
    props.investigations.currentInvestigation &&
    props.investigations.currentInvestigation.permissions &&
    !props.investigations.currentInvestigation.permissions.filter((perm) =>
      [PERMISSION.BUSINESS_READ, PERMISSION.ANALYTICS_DEPARTMENT].includes(perm)
    ).length > 0
  ) {
    history.push(ROUTE_401);
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Helmet title="Analytics Dashboard" />

      <Grid container spacing={6}>
        <Grid item xs={12} style={{ color: "white" }}>
          <SectionHeader section="analytics" />
        </Grid>

        {/* Demo investigations render synthetic ("fake") analytics on the frontend.
            Surface a subtle indicator so a sales/onboarding viewer never mistakes the
            sample charts for real patient data. Keyed off the same `demo` flag that
            drives the mock datasets. */}
        {props.investigations?.currentInvestigation?.demo && (
          <Grid item xs={12}>
            <Tooltip title={<Translate id="hospital.analytics.demo_indicator_tooltip" />} arrow>
              <Chip
                icon={<InfoOutlinedIcon />}
                label={<Translate id="hospital.analytics.demo_indicator" />}
                size="small"
                variant="outlined"
                color="warning"
              />
            </Tooltip>
          </Grid>
        )}

        <Grid item xs={12}>
          {visibleTabs.length > 0 ? (
            <AnalyticsHeader
              visibleTabs={visibleTabs}
              activeTabId={activeTabId}
              onTabChange={setActiveTabId}
            />
          ) : (
            <Box sx={{ background: 'white', p: 3 }}>
              <Typography>
                <Translate id="hospital.analytics.no_sections_available" />
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} style={{ background: 'white', padding: '2rem' }}>
          <Grid container justifyContent="between" spacing={3}>
            {renderSelectors()}
          </Grid>
        </Grid>
      </Grid>

      {visibleTabs.length > 0 && activeTabId && (
        <TabContent
          activeTabId={activeTabId}
          loading={props.investigations?.loading || !props.investigations?.currentInvestigation}
          startDate={startDate}
          endDate={endDate}
          investigation={props.investigations?.currentInvestigation}
          departmentSelected={departmentSelected}
          departments={departments}
        />
      )}
    </React.Fragment>
  );
}

Analytics.propTypes = {
  investigations: PropTypes.object
};

const mapStateToProps = (state) => ({
  investigations: state.investigations
});

export default withTheme(withLocalize(connect(mapStateToProps, null)(Analytics)));
